import { z } from 'zod';
import { moneySchema } from '../money';

/**
 * Zahlungsplan (ADR 0077) — löst die frühere unstrukturierte Anzahlungs-/
 * Schlussrechnungslogik (`createAdvanceInvoice`/`createFinalInvoice`) ab.
 * Bewusst KEINE fachliche Unterscheidung zwischen Anzahlung und Teilrechnung
 * am erzeugten Beleg (der ist immer `kind: 'INVOICE'`) — `type` lebt nur am
 * Plan, als Label und um die letzte Position („Schlussrechnung") zu markieren.
 */
export const paymentScheduleItemTypeSchema = z.enum(['TEILRECHNUNG', 'SCHLUSSRECHNUNG']);
export type PaymentScheduleItemType = z.infer<typeof paymentScheduleItemTypeSchema>;

export const paymentTriggerTypeSchema = z.enum(['DATE', 'MILESTONE']);
export type PaymentTriggerType = z.infer<typeof paymentTriggerTypeSchema>;

export const paymentMilestoneSchema = z.enum([
  'VOR_LIEFERUNG',
  'NACH_LIEFERUNG',
  'NACH_MONTAGE',
  'CUSTOM',
]);
export type PaymentMilestone = z.infer<typeof paymentMilestoneSchema>;

export const paymentBasisSchema = z.enum(['PERCENT', 'AMOUNT']);
export type PaymentBasis = z.infer<typeof paymentBasisSchema>;

export const paymentScheduleItemStatusSchema = z.enum(['GEPLANT', 'BERECHNET', 'STORNIERT']);
export type PaymentScheduleItemStatus = z.infer<typeof paymentScheduleItemStatusSchema>;

/** Menschenlesbares Label eines Zahlungsplan-Status. */
export function paymentScheduleItemStatusLabel(status: PaymentScheduleItemStatus): string {
  switch (status) {
    case 'GEPLANT':
      return 'Geplant';
    case 'BERECHNET':
      return 'Fakturiert';
    case 'STORNIERT':
      return 'Storniert';
  }
}

const MILESTONE_LABELS: Record<'DE' | 'EN', Record<PaymentMilestone, string>> = {
  DE: {
    VOR_LIEFERUNG: 'vor Lieferung',
    NACH_LIEFERUNG: 'nach Lieferung',
    NACH_MONTAGE: 'nach Montage',
    CUSTOM: 'individuell',
  },
  // Belegsprache EN (Review 2026-09-28, Befund 9): landet in den Abzugszeilen.
  EN: {
    VOR_LIEFERUNG: 'before delivery',
    NACH_LIEFERUNG: 'after delivery',
    NACH_MONTAGE: 'after installation',
    CUSTOM: 'custom',
  },
};

/** Menschenlesbares Label eines Meilensteins (inkl. CUSTOM-Fallback), Standard deutsch. */
export function paymentMilestoneLabel(milestone: PaymentMilestone, language: 'DE' | 'EN' = 'DE'): string {
  return MILESTONE_LABELS[language][milestone];
}

function englishOrdinal(value: number): string {
  const lastTwo = value % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${value}th`;
  switch (value % 10) {
    case 1:
      return `${value}st`;
    case 2:
      return `${value}nd`;
    case 3:
      return `${value}rd`;
    default:
      return `${value}th`;
  }
}

/**
 * Bezeichnung einer Zahlungsplan-Position für den Beleg in seiner Sprache
 * (Review 2026-09-28, Befund 9). Die Bezeichnung wird zur Überschrift und zu
 * den Abzugszeilen der erzeugten Rechnung — auf einem englischen Beleg stand
 * sonst „Schlussrechnung". Übersetzt werden die Standard-Bezeichnungen
 * (Default-Plan, übliche Vorlagen); eigener Freitext bleibt wörtlich — ihn zu
 * übersetzen hieße raten.
 */
export function localizePaymentScheduleLabel(label: string, language: 'DE' | 'EN'): string {
  if (language !== 'EN') return label;
  const text = label.trim();
  const fixed: Record<string, string> = {
    schlussrechnung: 'Final Invoice',
    anzahlung: 'Down Payment',
    anzahlungsrechnung: 'Down Payment Invoice',
    teilrechnung: 'Partial Invoice',
    teilzahlung: 'Partial Payment',
    abschlagsrechnung: 'Progress Invoice',
  };
  const exact = fixed[text.toLowerCase()];
  if (exact) return exact;
  const numbered = /^(\d+)\.\s*(Teilrechnung|Abschlagsrechnung|Anzahlung|Teilzahlung)$/i.exec(text);
  if (numbered) return `${englishOrdinal(Number(numbered[1]))} ${fixed[numbered[2].toLowerCase()]}`;
  const trailing = /^(Teilrechnung|Abschlagsrechnung|Teilzahlung)\s+(\d+)$/i.exec(text);
  if (trailing) return `${fixed[trailing[1].toLowerCase()]} ${trailing[2]}`;
  return label;
}

/**
 * Eine GEPLANT-Zeile im Bulk-Edit. Bereits `BERECHNET`e Positionen werden
 * NICHT über dieses Schema verändert — historisch fixiert (§2), sie werden
 * einfach unverändert mitgeschickt (der Server verwirft abweichende Werte
 * für diese Positionen, statt sie zu übernehmen).
 */
export const paymentScheduleItemInputSchema = z
  .object({
    /** Nur bei bereits bestehenden Positionen gesetzt — neue Zeilen ohne id. */
    id: z.string().uuid().optional(),
    type: paymentScheduleItemTypeSchema,
    label: z.string().trim().min(1).max(200),
    triggerType: paymentTriggerTypeSchema,
    triggerDate: z.string().datetime().optional(),
    milestone: paymentMilestoneSchema.optional(),
    milestoneCustomText: z.string().trim().max(200).optional(),
    basis: paymentBasisSchema,
    percentage: z.number().gt(0).max(100).optional(),
    amount: moneySchema.optional(),
  })
  .refine((value) => value.triggerType !== 'DATE' || value.triggerDate != null, {
    message: 'Bei triggerType DATE ist triggerDate erforderlich.',
  })
  .refine((value) => value.triggerType !== 'MILESTONE' || value.milestone != null, {
    message: 'Bei triggerType MILESTONE ist milestone erforderlich.',
  })
  .refine((value) => value.milestone !== 'CUSTOM' || Boolean(value.milestoneCustomText?.trim()), {
    message: 'Bei milestone CUSTOM ist milestoneCustomText erforderlich.',
  })
  .refine((value) => (value.basis === 'PERCENT') === (value.percentage != null), {
    message: 'Bei basis PERCENT ist percentage erforderlich (und nur dann).',
  })
  .refine((value) => (value.basis === 'AMOUNT') === (value.amount != null), {
    message: 'Bei basis AMOUNT ist amount erforderlich (und nur dann).',
  });
export type PaymentScheduleItemInput = z.infer<typeof paymentScheduleItemInputSchema>;

export const replacePaymentScheduleItemsSchema = z.object({
  items: z.array(paymentScheduleItemInputSchema).min(1).max(50),
});
export type ReplacePaymentScheduleItemsInput = z.infer<typeof replacePaymentScheduleItemsSchema>;

export const paymentScheduleItemResponseSchema = z.object({
  id: z.string().uuid(),
  position: z.number().int(),
  type: paymentScheduleItemTypeSchema,
  label: z.string(),
  triggerType: paymentTriggerTypeSchema,
  triggerDate: z.string().datetime().nullable(),
  milestone: paymentMilestoneSchema.nullable(),
  milestoneCustomText: z.string().nullable(),
  basis: paymentBasisSchema,
  percentage: z.number().nullable(),
  amountNet: moneySchema,
  status: paymentScheduleItemStatusSchema,
  invoiceId: z.string().uuid().nullable(),
  /** Denormalisiert für die Statusspalte (§6) — server-seitig aufgelöst. */
  invoiceNumber: z.string().nullable(),
  invoiceDate: z.string().datetime().nullable(),
  /** true, sobald die verlinkte Rechnung als bezahlt gilt (Status PAID). */
  isPaid: z.boolean(),
});
export type PaymentScheduleItemResponse = z.infer<typeof paymentScheduleItemResponseSchema>;

export const paymentScheduleResponseSchema = z.object({
  orderId: z.string().uuid(),
  orderNumber: z.string(),
  orderNet: moneySchema,
  items: z.array(paymentScheduleItemResponseSchema),
  /** Summe amountNet (ohne STORNIERT) == orderNet? Treibt die Fakturasperre. */
  consistent: z.boolean(),
  enforceOrder: z.boolean(),
});
export type PaymentScheduleResponse = z.infer<typeof paymentScheduleResponseSchema>;
